import type { PageSeo } from './seoConfig';
import { buildJsonLd, buildShareImageUrl, getPublicBaseUrl } from './seoConfig';

const JSON_LD_ID = 'bmcs-seo-jsonld';

function ensureMetaByName(name: string): HTMLMetaElement {
  const found = Array.from(document.querySelectorAll('meta[name]')).find(
    (n) => n.getAttribute('name') === name
  ) as HTMLMetaElement | undefined;
  if (found) return found;
  const el = document.createElement('meta');
  el.setAttribute('name', name);
  document.head.appendChild(el);
  return el;
}

function ensureMetaByProperty(property: string): HTMLMetaElement {
  const found = Array.from(document.querySelectorAll('meta[property]')).find(
    (n) => n.getAttribute('property') === property
  ) as HTMLMetaElement | undefined;
  if (found) return found;
  const el = document.createElement('meta');
  el.setAttribute('property', property);
  document.head.appendChild(el);
  return el;
}

function ensureCanonicalLink(): HTMLLinkElement {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  return el;
}

function setRobotsMeta(noIndex: boolean): void {
  const existing = document.querySelector('meta[name="robots"]');
  if (noIndex) {
    const el = existing ? (existing as HTMLMetaElement) : ensureMetaByName('robots');
    el.setAttribute('content', 'noindex, nofollow');
  } else if (existing) {
    existing.remove();
  }
}

function setJsonLd(publicBaseUrl: string): void {
  let el = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
  if (!publicBaseUrl) {
    el?.remove();
    return;
  }
  if (!el) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = JSON_LD_ID;
    document.head.appendChild(script);
    el = script;
  }
  el.textContent = JSON.stringify(buildJsonLd(publicBaseUrl));
}

export function applyPageSeo(pathname: string, seo: PageSeo): void {
  const base = getPublicBaseUrl();
  const canonicalUrl = base ? `${base}${pathname === '/' ? '' : pathname}` : '';

  document.title = seo.title;

  ensureMetaByName('description').setAttribute('content', seo.description);

  setRobotsMeta(seo.noIndex);

  if (canonicalUrl) {
    ensureCanonicalLink().setAttribute('href', canonicalUrl);
  }

  const imageUrl = buildShareImageUrl();

  ensureMetaByProperty('og:type').setAttribute('content', 'website');
  ensureMetaByProperty('og:title').setAttribute('content', seo.title);
  ensureMetaByProperty('og:description').setAttribute('content', seo.description);
  if (canonicalUrl) {
    ensureMetaByProperty('og:url').setAttribute('content', canonicalUrl);
  }
  ensureMetaByProperty('og:image').setAttribute('content', imageUrl);
  ensureMetaByProperty('og:locale').setAttribute('content', 'en_ZA');
  ensureMetaByProperty('og:site_name').setAttribute('content', 'BMCS');

  ensureMetaByName('twitter:card').setAttribute('content', 'summary_large_image');
  ensureMetaByName('twitter:title').setAttribute('content', seo.title);
  ensureMetaByName('twitter:description').setAttribute('content', seo.description);
  ensureMetaByName('twitter:image').setAttribute('content', imageUrl);

  if (seo.noIndex) {
    document.getElementById(JSON_LD_ID)?.remove();
  } else {
    setJsonLd(base);
  }
}
